import React, { useEffect, useContext } from 'react'
import { FullPageLoader } from '@deal/components'
import { Experiment } from '@deal/experiment-js'
import {
  useCreatePathFlowMutation,
  CreatePathFlowMutation,
  CreatePathFlowInput,
  PathFlowFragment
} from '#src/generated/graphql'
import { AnalyticsContext } from '#src/containers/Analytics'
import { IdentityContext } from '#src/containers/Identity'
import { derivePathLayout } from '#src/services/path'
import { PathLayouts } from '#src/typings'
import { HandlePathCreatedProps } from '../'
import { useHistory } from 'react-router-dom'
import { setRegistrationDepartment } from '#src/services/registrationValues'
import { ifBridgeDefined } from '#src/services/dealNativeBridge'
import pickBy from 'lodash/pickBy'

export interface PathCategory {
  id: string
  slug: string | null
}

interface CreatePathProps {
  pathSlug?: string
  categorySlug?: string
  prefillData?: CreatePathFlowInput
  pathLayout?: PathLayouts
  onPathCompleted: (flow: PathFlowFragment) => void
  onPathCreated: (pathCreated: HandlePathCreatedProps) => void
}

interface CreatePathFlowMutationComponentProps {
  pathSlug?: string
  categorySlug?: string
  pathLayout: PathLayouts
  onPathCreated: (response: CreatePathFlowMutation, pathLayout: PathLayouts) => void
  prefillData?: CreatePathFlowInput
}

const CreatePathFlowMutationComponent: React.SFC<CreatePathFlowMutationComponentProps> = ({
  onPathCreated,
  pathLayout,
  pathSlug,
  categorySlug,
  prefillData
}) => {
  const [createPathMutation] = useCreatePathFlowMutation({
    onCompleted: response => onPathCreated(response, pathLayout)
  })

  useEffect(() => {
    /**
     * The bridge definition check makes a window reference and needs
     * to happen on mount
     */
    let nativeAppData: {
      uniqueDeviceId?: string
      applicationId?: string
    } = {}

    ifBridgeDefined(bridge => {
      nativeAppData.uniqueDeviceId = bridge.deviceUniqueId
      nativeAppData.applicationId = bridge.appBundleId
    })

    let createCriteria = {}
    if (pathSlug) {
      createCriteria = {
        pathSchemaSlug: pathSlug
      }
    } else if (categorySlug) {
      createCriteria = {
        categorySlug
      }
    } else {
      throw new Error('No path slug or category slug passed when attempting to create a path')
    }

    const sanitizedPrefillData = pickBy(prefillData, v => v !== '')

    createPathMutation({
      variables: {
        input: {
          ...sanitizedPrefillData,
          ...createCriteria,
          uniqueDeviceId: nativeAppData.uniqueDeviceId,
          applicationId: nativeAppData.applicationId,
          timeZoneOffsetInMinutes: new Date().getTimezoneOffset()
        }
      }
    })
  }, [])

  return <FullPageLoader />
}

const CreatePath: React.SFC<CreatePathProps> = ({
  pathSlug,
  categorySlug,
  prefillData,
  pathLayout,
  onPathCompleted,
  onPathCreated
}) => {
  const analytics = useContext(AnalyticsContext)
  const { location } = useHistory()
  const { refetch: refetchIdentity } = useContext(IdentityContext)
  const queryStringParams = new URLSearchParams(location.search)

  /**
   * After creating the path:
   *
   *  1. Check if a new user was registered during path creation
   *    (1a) If so, update the user's identity on the client
   *    (1b) If so, and track the registration
   *
   *  2. Check if the path is already completed (possible if enough prefill data is passed)
   *    (2a) If so, short circuit and call the onPathCompleted callback
   *    (2b) Otherwise, call the onPathCreated callback
   */
  const handlePathCreated = (response: CreatePathFlowMutation, pathLayout: PathLayouts) => {
    const { createPathFlow } = response

    if (!createPathFlow.flow) {
      throw new Error('Error creating path flow')
    }

    const createdPathFlow = createPathFlow.flow
    const createdPathDepartment =
      createdPathFlow.category &&
      createdPathFlow.category.department &&
      createdPathFlow.category.department.slug

    /**
     * This is a last ditch effort to make sure we set this prior to registration. There
     * is a very good chance that the department slug was set on either a landing page or
     * engagement channel variation, which is why we don't want to override.
     */
    setRegistrationDepartment(createdPathDepartment)

    if (createdPathDepartment) {
      analytics?.setDepartment(createdPathDepartment)
    }

    // 1. Check if a user was registered during path creation
    if (createPathFlow.newUserRegistered && refetchIdentity) {
      // 1a. Update the user's identity on the client
      refetchIdentity().then(result => {
        const myself = result.data.me

        // 1b. Track it
        if (analytics && myself) {
          analytics.events.userRegistered({
            user_id: myself.id,
            context: 'path-create'
          })
        }
        return null
      })
    }

    if (createdPathFlow.completed) {
      // 2b. Call onPathCompleted
      return onPathCompleted(createdPathFlow)
    }

    const flowSlug = createdPathFlow.slug
    const flowId = createdPathFlow.id
    const firstPageSlug = createdPathFlow.pagesConnection.edges[0]
      ? createdPathFlow.pagesConnection.edges[0].node.slug
      : undefined

    // 2b. Call onPathCreated
    onPathCreated({
      flowState: {
        pathSlug: flowSlug,
        pageSlug: firstPageSlug,
        flowId
      },
      pathLayout
    })
  }

  /**
   * Check to see if we're forcing a path layout since experiment overrides
   * don't persist throughout path creation
   */
  const pathLayoutFromUrl = queryStringParams.get('forcePathLayout')
  const forcedPathLayout = pathLayoutFromUrl && derivePathLayout(pathLayoutFromUrl)

  if (categorySlug) {
    return (
      <Experiment
        id={`${categorySlug}-path-layout`}
        options={{
          skip: pathLayout !== undefined
        }}
        default="default"
      >
        {({ result: pathLayoutResult, loading }) => {
          if (loading) {
            return <FullPageLoader />
          }

          /**
           * Honor in the order of:
           * 1. A url override for a path layout
           * 2. A prop based path layout
           * 3. A path layout derived from the experiment result
           *    a. The derive method will result in a PathLayouts.DEFAULT on the default of the switch case
           */
          const finalPathLayout =
            forcedPathLayout || pathLayout || derivePathLayout(pathLayoutResult)

          return (
            <CreatePathFlowMutationComponent
              onPathCreated={handlePathCreated}
              prefillData={prefillData}
              categorySlug={categorySlug}
              pathLayout={finalPathLayout}
            />
          )
        }}
      </Experiment>
    )
  } else if (pathSlug) {
    /**
     * Honor in the order of:
     * 1. A url override for a path layout
     * 2. A prop based path layout
     * 3. The default path layout
     */
    const finalPathLayout = forcedPathLayout || pathLayout || PathLayouts.DEFAULT

    return (
      <CreatePathFlowMutationComponent
        onPathCreated={handlePathCreated}
        prefillData={prefillData}
        pathSlug={pathSlug}
        pathLayout={finalPathLayout}
      />
    )
  }

  throw new Error('Insufficient parameters passed to create a new path')
}

export default CreatePath
